import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux';
import Select from "react-select"
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Grid, Stack, TextField, Typography, Button, InputAdornment, IconButton, Tooltip, CircularProgress, Icon } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
// icons
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from 'common/customIcons/search';
import DeleteIcon from "common/customIcons/delete";
// utils and helpers
import { debounce } from "utilities/common";
import { assignProduct, deleteBox, getAllProducts, getBoxes, getUnassignedProducts } from "../../../redux/slices/hyperwolf/inventoryDistribution";
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from "../../../redux/slices/modalSlice";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import { showToastMsg } from "../distributionManagement/addRegionKit";
import MDButton from "components/MDButton";
import { BrandFilter } from "common/filters";
import { SubCategoryFilter } from "common/filters";
import CommonAgGridTable from "components/tables/CommonAgGridTable";
import BoxProductsAgGridTable from 'components/tables/boxProductsTable';
import NoDataFound from "common/component/NoDataFound";
import { defaultColDef } from 'components/tables/boxProductsTable';
import { TableLoader } from "components/tables/AgGridAccordionChildScrollingTable";

export const productsTypeOptions = [
  { value: "all", label: "All" },
  { value: "unassigned", label: "Unassigned" },
  { value: "assigned", label: "Assigned" },
]

const ManageBoxes = () => {
  const gridRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const { tab, box } = Object.fromEntries(searchParams.entries());

  const [activeTab, setActiveTab] = useState(tab || 'boxes'); // 'boxes' or 'products'
  const [inputFilters, setInputFilters] = useState({});
  const [customFilters, setCustomFilters] = useState({ productsType: "all" });
  const [refreshTable, setRefreshTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  const columnDefs = [
    {
      headerName: 'Name',
      field: 'name',
      suppressMenu: false,
      flex: 2,
      sortable: true,
    },
    {
      headerName: 'Description',
      field: 'description',
      suppressMenu: false,
      flex: 2.5,
      sortable: true,
      cellRenderer: (params) => {
        const description = params?.data?.description || "-";
        return (
          <Tooltip title={description} arrow>
            <span className='text-truncate'>{description}</span>
          </Tooltip>
        );
      }
    },
    {
      headerName: 'No. of Products',
      field: 'productCount',
      suppressMenu: false,
      flex: 1,
      sortable: true,
    },
    {
      headerName: 'Actions',
      field: 'actions',
      suppressMenu: false,
      flex: 1,
      sortable: false,
      cellRenderer: (props) => {
        if (!props?.data?._id) return null;
        return (
          <Box display="inline-flex">
            <IconButton
              onClick={() => {
                navigate(`/hyperwolf/boxes/view-box/${props?.data?._id}`)
              }}
              color="primary"
              size="small"
              variant="primary"
            >
              <VisibilityIcon width="16px" height="16px" />
            </IconButton>
            <IconButton
              onClick={() => {
                navigate(`/hyperwolf/boxes/update-box/${props?.data?._id}`)
              }}
              color="primary"
              size="small"
              variant="primary"
              sx={{ marginLeft: 1 }}
            >
              <Icon>edit</Icon>
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                dispatch(openCustomModalDialog({
                  modalData: { deleteHandler: handleDelete, deleteId: props.data?._id, deleteType: "rule", },
                  modal: "DELETE_MODAL",
                })
                );
              }}
              color="error"
              size="small"
              variant="primary"
              sx={{ marginLeft: 1 }}
            >
              <DeleteIcon width="16px" height="16px" />
            </IconButton>
          </Box>
        );
      },
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      minWidth: 120
    },
  ];

  const productsColumnDefs = [
    {
      headerName: 'Product Name/SKU',
      field: 'adminProductName',
      key: 'adminProductName',
      suppressMenu: false,
      flex: 2,
      minWidth: 300,
      sortable: true,
      checkboxSelection: true,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { adminProductName, sku, assets } = params?.data || {}
        const { thumbURL, name } = assets?.[0] || {}

        return <Box style={{ display: "flex", gap: "10px" }}>
          <img src={thumbURL} alt={name} width={30} height={30} />
          <Box>
            <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{adminProductName}</Typography>
            {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px", padding: "0" }}>{sku}</Typography>}
          </Box>
        </Box>
      }
    },
    {
      headerName: 'Category',
      field: 'category.name',
      suppressMenu: false,
      sortable: true,
      flex: 1.5,
      minWidth: 200,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { category } = params?.data || {}
        return <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{category?.name}</Typography>
      }
    },
    {
      headerName: 'Brand',
      field: 'brand.name',
      suppressMenu: false,
      sortable: true,
      flex: 1.5,
      minWidth: 200,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { brand } = params?.data || {}
        return <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{brand?.name}</Typography>
      }
    },
    {
      headerName: 'Box',
      field: 'box.name',
      suppressMenu: false,
      sortable: true,
      flex: 1.5,
      minWidth: 200,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { box } = params?.data || {}
        return <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{box?.name || "-"}</Typography>
      }
    },
    {
      headerName: 'Available Quantity',
      field: 'quantity',
      suppressMenu: false,
      sortable: true,
      flex: 0.75,
      minWidth: 80,
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
    },
    // {
    //   headerName: 'Retail Price',
    //   field: 'totalPrice',
    //   suppressMenu: false,
    //   sortable: false,
    //   flex: 0.75,
    //   minWidth: 80,
    //   valueGetter: ({ data }) => `$${data?.totalPrice || 0}`,
    //   cellStyle: { textAlign: 'right', justifyContent: 'end' },
    //   headerClass: 'right-aligned-cell-header',
    // },
    // {
    //   headerName: 'Actions',
    //   field: 'actions',
    //   suppressMenu: false,
    //   flex: 2,
    //   sortable: false,
    //   cellRenderer: (props) => {
    //     if (!props?.data?._id) return null;
    //     return (
    //       <Box display="inline-flex" alignItems="center">
    //         <IconButton
    //           onClick={(e) => {
    //             e.stopPropagation();
    //             dispatch(openCustomModalDialog({
    //               modalData: { deleteHandler: handleDelete, deleteId: props.data?._id, deleteType: "rule", },
    //               modal: "ASSIGN_PRODUCTS_TO_BOX",
    //             })
    //             );
    //           }}
    //           color="error"
    //           size="small"
    //           variant="primary"
    //           sx={{ marginLeft: 1 }}
    //         >
    //           <DeleteIcon width="16px" height="16px" />
    //         </IconButton>
    //       </Box>
    //     );
    //   },
    // },
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCustomFilters({});
    setInputFilters({});
    setSelectedProducts([]);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }

  const handleDebounce = useCallback(debounce((event) => {
    const { name, value } = event.target;

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
    gridRef.current?.api?.setGridOption('quickFilterText', value);
  }, 1000), []);

  const handleClearFilters = () => {
    setInputFilters({ search: customFilters.search || "" });
    setCustomFilters({ search: customFilters.search || "" });
    setRefreshTable(prev => !prev);
  }

  const handleAssignProducts = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const request = {
        boxId: box,
        productIds: isAllChecked ? [] : selectedProducts,
        isAllSelected: isAllChecked,
        ...customFilters
      };
      const response = await dispatch(assignProduct(request)).unwrap();

      if (response?.message) {
        showToastMsg(response?.message || "Success", "success");
        setRefreshTable(prev => !prev);
        handleGetAllProducts();
      };
    } catch (error) {
      showToastMsg(error, "error")
    } finally {
      setIsLoading(false);
    }
  }

  const handleDelete = async (id) => {
    try {
      dispatch(setModalLoader(true));

      const res = await dispatch(deleteBox(id)).unwrap();
      setRefreshTable(prev => !prev)

      showToastMsg(res?.message || "Success", "success")
      dispatch(closeCustomModalDialog());
    } catch (err) {
      dispatch(setModalLoader(false));
      showToastMsg(err, "error")
    };
  };

  const handleSelectAll = (e) => {
    const { checked } = e.target;
    setIsAllSelected(checked);
    setIsAllChecked(checked);
    setSelectedProducts([]);

    if (checked) {
      gridRef.current?.api?.selectAllFiltered();
    } else {
      gridRef.current?.api?.forEachNodeAfterFilter((node) => node.setSelected(false));
    }
  };

  const handleGetAllProducts = async () => {
    try {
      if (gridRef.current) {
        gridRef.current?.api?.showLoadingOverlay();
      }

      const response = await dispatch(getUnassignedProducts({ limit: 100000, page: 1 })).unwrap();
      const { data } = response || {};

      if (!data?.length) {
        gridRef.current?.api?.showNoRowsOverlay();
      } else {
        gridRef.current?.api?.hideOverlay();
      }
      if (data) {
        setAllProducts(data || [])
      }
    } catch (error) {
      showToastMsg(error, "error");
    }
  }

  const updateIsAllChecked = useCallback(() => {
    const selectableDisplayedRows = [];
    gridRef.current?.api?.forEachNodeAfterFilterAndSort((node) => {
      // if (!node.data.isAssigned) {
      selectableDisplayedRows.push(node);
      // }
    });

    const allSelectableSelected = selectableDisplayedRows.length > 0 && selectableDisplayedRows.every(node => node.isSelected());
    setIsAllChecked(allSelectableSelected);
  }, []);

  const onSelectionChanged = useCallback(debounce(() => {
    const selectedRows = gridRef.current?.api?.getSelectedRows();
    const selectedIds = selectedRows?.map(row => row.productId) || [];
    setSelectedProducts(selectedIds);

    // Update Select All checkbox state based on selection
    updateIsAllChecked();
  }, 100), [updateIsAllChecked]);

  const isExternalFilterPresent = useCallback(() => {
    return Object.values(customFilters).some(Boolean);
  }, [customFilters]);

  const doesExternalFilterPass = useCallback((node) => {
    const { categoryId, brandId, productsType } = customFilters;
    const data = node.data;

    if (!data) return false;

    if (categoryId) {
      const catId = data.category?.id;
      if (catId !== categoryId) return false;
    }

    if (brandId) {
      const bId = data.brand?.id;
      if (bId !== brandId) return false;
    }

    if (productsType === "assigned") {
      if (!data.isAssigned && !data.isSelected) return false;
    } else if (productsType === "unassigned") {
      if (data.isAssigned || data.isSelected) return false;
    }

    return true;
  }, [customFilters]);

  useEffect(() => {
    if (activeTab === 'products') {
      handleGetAllProducts();
    }
  }, [activeTab])

  return (
    <CommonLayout>
      {/* Tabs */}
      <Box pt={{ xs: 2, sm: 3, lg: 4 }} pl={{ xs: 2, sm: 3, lg: 4 }} pr={{ xs: 2, sm: 3, lg: 4 }} className="cannabinoids-tabs">
        <Stack direction='row' gap={1} sx={{ background: '#ffffff' }}>
          <Button
            variant='outlined'
            className={activeTab === 'boxes' ? 'active' : ''}
            sx={{ color: '#2E2E3A' }}
            onClick={() => handleTabChange('boxes')}
          >
            Boxes
          </Button>
          <Button
            variant='outlined'
            className={activeTab === 'products' ? 'active' : ''}
            sx={{ color: '#2E2E3A' }}
            onClick={() => handleTabChange('products')}
          >
            Products
          </Button>
        </Stack>
      </Box>

      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant='h4' fontWeight="semibold">{activeTab === 'boxes' ? "Boxes" : "Products"}</Typography>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>

              {/* Search Filter */}
              <TextField
                className="custom-search-field"
                placeholder="Search"
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                    <IconButton size='small'>
                      <SearchIcon width='18px' height='18px' />
                    </IconButton>
                  </InputAdornment>
                }}
                name="search"
                value={inputFilters.search || ""}
                onChange={event => {
                  const { value } = event.target;
                  setInputFilters({ ...inputFilters, search: value })
                  handleDebounce(event)
                }}
                style={{ minWidth: "300px" }}
              />

              {/* Actions */}
              {activeTab === 'boxes' ? <Link to="/hyperwolf/boxes/create-box">
                <MDButton
                  style={{ height: "100%", minWidth: "180px" }}
                  startIcon={<AddRoundedIcon fontSize="large" />}
                  variant="contained"
                  color="primary"
                >
                  Create Box
                </MDButton>
              </Link> :
                <MDButton
                  style={{ height: "100%", minWidth: "160px" }}
                  startIcon={isLoading ? "" : <AddRoundedIcon fontSize="large" />}
                  variant="contained"
                  color="primary"
                  disabled={!selectedProducts?.length}
                  onClick={() => {
                    if (!selectedProducts?.length) return;

                    if (box) {
                      handleAssignProducts()
                    } else {
                      dispatch(openCustomModalDialog({
                        modal: 'ASSIGN_PRODUCTS_TO_BOX',
                        modalData: { customFilters, isAllChecked, selectedProducts, setSelectedProducts, setRefreshRules: setRefreshTable, handleGetAllProducts }
                      }))
                    }
                  }}
                >
                  {isLoading ? <CircularProgress size={22} color="light" /> : "Assign Products"}
                </MDButton>
              }
            </Stack>
          </Grid>
        </Grid>

        {/* Filters */}
        {activeTab === 'products' && (
          <Box className='cus-filter-sec' mb={2}>
            <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
              <Grid item xs={12} lg={3} xxxl={2}>
                <Select
                  className='cus-react-select'
                  name="productsType"
                  value={productsTypeOptions?.find(type => type.value === customFilters?.productsType)}
                  onChange={selectedOption => {
                    handleChange({
                      target: {
                        name: "productsType",
                        value: selectedOption ? selectedOption.value : ""
                      }
                    });
                  }}
                  options={productsTypeOptions}
                  placeholder="All Products"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: 40,
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                  components={{ IndicatorSeparator: null }}
                />
              </Grid>
              <Grid item xs={12} lg={3} xxxl={2}>
                <SubCategoryFilter
                  value={customFilters?.categoryId}
                  handleChange={handleChange}
                  placeholder={"Categories"}
                  selectAllLabel={"All Categories"}
                />
              </Grid>
              <Grid item xs={12} lg={3} xxxl={2}>
                <BrandFilter
                  value={customFilters?.brandId}
                  handleChange={handleChange}
                  placeholder="Brands"
                  selectAllLabel="All"
                />
              </Grid>
              <Grid item xs={12} lg={3} xxxl={2}>
                <MDButton
                  sx={{ minWidth: "120px", fontWeight: "normal" }}
                  type="button"
                  variant="outlined"
                  color="dark"
                  disabled={!Object.values(customFilters || {})?.filter(Boolean)?.length}
                  onClick={handleClearFilters}
                >
                  Clear All
                </MDButton>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Boxes */}
        {activeTab === 'boxes' && (
          <div className="ag-grid-table-white table-nodata-size">
            <div className="ag-theme-quartz" style={{ height: "75vh", width: "100%" }}>
              <CommonAgGridTable
                pagination={false}
                columnDefs={columnDefs}
                getDataMethod={getBoxes}
                refreshTable={refreshTable}
                customFilters={{ ...customFilters }}
                perPage={20}
                onCellClicked={(props) => {
                  // Prevent row click if click originated from the actions column
                  if (props?.column && (props.column.getColId() === "actions")) {
                    return;
                  }
                  navigate(`/hyperwolf/boxes/view-box/${props?.data?._id}`)
                }}
              />
            </div>
          </div>
        )}

        {/* Unassigned Products */}
        {
          activeTab === 'products' && (
            <div className="ag-grid-table-white table-nodata-size">
              {/* Select all */}
              <Box display="flex" justifyContent="space-between">
                <Box className="form-check px-3 pb-2 d-flex align-item-center gap-2 text-14">
                  <>
                    <input
                      className="form-check-input cur-pointer"
                      type="checkbox"
                      id={"selectAll"}
                      name={"isAllSelected"}
                      checked={isAllChecked}
                      onChange={handleSelectAll}
                    />
                    <label className="form-check-label" htmlFor="selectAll">
                      Select All
                    </label>
                  </>
                </Box>
                <Box className="form-check px-3 pb-2 d-flex align-item-center gap-2 text-14">
                  <label className="form-check-label" htmlFor="selectAll">
                    Selected Products: {selectedProducts?.length || 0}
                  </label>
                </Box>
              </Box>

              <div className="ag-theme-quartz" style={{ height: "66vh", width: "100%" }}>
                <AgGridReact
                  ref={gridRef}
                  className="ag-grid--table"
                  pagination={false}
                  rowData={allProducts?.length > 0 ? allProducts : null}
                  noRowsOverlayComponent={NoDataFound}
                  loadingOverlayComponent={TableLoader}
                  columnDefs={productsColumnDefs}
                  rowModelType={"clientSide"}
                  headerHeight={36}
                  rowHeight={56}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  defaultColDef={{ ...defaultColDef }}
                  isExternalFilterPresent={isExternalFilterPresent}
                  doesExternalFilterPass={doesExternalFilterPass}
                  onSelectionChanged={onSelectionChanged}
                  onFilterChanged={updateIsAllChecked}
                  onRowDataUpdated={updateIsAllChecked}
                />
                {/* <BoxProductsAgGridTable
                  perPage={5000}
                  pagination={false}
                  columnDefs={productsColumnDefs}
                  getDataMethod={getAllProducts}
                  refreshTable={refreshTable}
                  customFilters={{ ...customFilters }}
                  selectedProducts={selectedProducts}
                  setSelectedProducts={setSelectedProducts}
                  isSelectedAll={isAllSelected}
                  isAllChecked={isAllChecked}
                  setIsAllChecked={setIsAllChecked}
                /> */}
              </div>
            </div>
          )
        }
      </Box>
    </CommonLayout>
  )
}

export default ManageBoxes